import { useQuery, useMutation } from "@tanstack/react-query";
import queryString from "query-string";

import { api } from "@services/index";
import {
  ApiErrorModel,
  AssetFormInterface,
  AssetFormInterfaceDto,
  AssetDisposalFormInterface,
  AssetDisposalFormInterfaceDto,
  AssetModel,
  AssetModelDto,
  AssetFileModel,
  AssetFileModelDto,
  AssetAttachmentModel,
} from "@interfaces/index";
import {
  formatDateForApi,
  ASSET_NO_AGREEMENT_ATTACHMENTS_NAME,
  ASSET_NO_INVOICE_ATTACHMENTS_NAME,
} from "@utils/index";

const prepareAssetFiles = ({
  filesAgreement,
  notApplicableAgreement,
  uploadLaterAgreement,
  filesInvoice,
  notApplicableInvoice,
  uploadLaterInvoice,
}: {
  filesAgreement?: AssetFileModel[];
  notApplicableAgreement?: boolean;
  uploadLaterAgreement?: boolean;
  filesInvoice?: AssetFileModel[];
  notApplicableInvoice?: boolean;
  uploadLaterInvoice?: boolean;
}) => {
  const agreements: AssetFileModelDto[] = !filesAgreement?.length
    ? []
    : filesAgreement?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
        type: file.type,
        not_applicable: false,
        upload_later: false,
      }));
  if (!agreements.length && (notApplicableAgreement || uploadLaterAgreement)) {
    agreements.push({
      key: ASSET_NO_AGREEMENT_ATTACHMENTS_NAME,
      uuid: ASSET_NO_AGREEMENT_ATTACHMENTS_NAME,
      original_name: ASSET_NO_AGREEMENT_ATTACHMENTS_NAME,
      type: "loan_agreement",
      not_applicable: !!notApplicableAgreement,
      upload_later: !!uploadLaterAgreement,
    });
  }

  const invoices: AssetFileModelDto[] = !filesInvoice?.length
    ? []
    : filesInvoice?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
        type: file.type,
        not_applicable: false,
        upload_later: false,
      }));
  if (!invoices.length && (notApplicableInvoice || uploadLaterInvoice)) {
    agreements.push({
      key: ASSET_NO_INVOICE_ATTACHMENTS_NAME,
      uuid: ASSET_NO_INVOICE_ATTACHMENTS_NAME,
      original_name: ASSET_NO_INVOICE_ATTACHMENTS_NAME,
      type: "invoice",
      not_applicable: !!notApplicableInvoice,
      upload_later: !!uploadLaterInvoice,
    });
  }

  return [...agreements, ...invoices];
};

const prepareAssetToModel = (asset: AssetModelDto): AssetModel => ({
  uniqueId: asset.unique_id,
  name: asset.name,
  type: asset.type,
  purchaseType: asset.purchase_type,
  date: asset.date,
  firstLetAt: asset.first_let_at,
  cost: asset.cost ? String(asset.cost) : undefined,
  jointShare: asset.joint_share ? String(asset.joint_share) : undefined,
  comment: asset.comment,
  disposal: !asset.disposal
    ? null
    : {
        cost: String(asset.disposal.cost || ""),
        date: asset.disposal.date,
        comment: asset.disposal.comment,
      },
  userBusiness: !asset.user_business
    ? undefined
    : {
        uniqueId: asset.user_business?.unique_id,
        name: asset.user_business?.name,
        type: asset.user_business?.type,
        tradingStartDate: asset.user_business?.trading_start_date,
      },
  attachments: [],
  uploadLaterAgreement: asset.attachments.some(
    ({ type, upload_later, original_name }) =>
      type === "loan_agreement" &&
      (original_name === null ||
        original_name === ASSET_NO_AGREEMENT_ATTACHMENTS_NAME) &&
      upload_later,
  ),
  notApplicableAgreement: asset.attachments.some(
    ({ type, not_applicable, original_name }) =>
      type === "loan_agreement" &&
      (original_name === null ||
        original_name === ASSET_NO_AGREEMENT_ATTACHMENTS_NAME) &&
      not_applicable,
  ),
  uploadLaterInvoice: asset.attachments.some(
    ({ type, upload_later, original_name }) =>
      type === "invoice" &&
      (original_name === null ||
        original_name === ASSET_NO_INVOICE_ATTACHMENTS_NAME) &&
      upload_later,
  ),
  notApplicableInvoice: asset.attachments.some(
    ({ type, not_applicable, original_name }) =>
      type === "invoice" &&
      (original_name === null ||
        original_name === ASSET_NO_INVOICE_ATTACHMENTS_NAME) &&
      not_applicable,
  ),
  isDefault: asset.is_default,
  policies: asset.policies,
});

export const useAssetGetAll = (search: string) => {
  const queryFn = async () => {
    const params = queryString.stringify({ search }, { skipEmptyString: true });
    const { data: dataDto } = await api.get<AssetModelDto[]>(
      `/assets?${params}`,
    );

    return dataDto.map((asset) => prepareAssetToModel(asset));
  };

  return useQuery<AssetModel[], ApiErrorModel>({
    queryKey: ["assetGetAll", search],
    queryFn,
    refetchOnWindowFocus: true,
    enabled: !!search,
  });
};

export const useAssetGetBusinessAssets = (businessId: string) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<AssetModelDto[]>(
      `/assets/business/${businessId}`,
    );

    return dataDto.map((asset) => prepareAssetToModel(asset));
  };

  return useQuery<AssetModel[], ApiErrorModel>({
    queryKey: ["assetGetBusinessAssets", businessId],
    queryFn,
    enabled: !!businessId,
  });
};

export const useAssetGetOne = (id: string) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<AssetModelDto>(`/assets/${id}`);

    const agreementsItems = dataDto.attachments.filter(
      ({ type }) => type === "loan_agreement",
    );
    let notApplicableAgreement,
      uploadLaterAgreement,
      agreements: AssetAttachmentModel[] = [];
    if (
      agreementsItems[0]?.original_name === null ||
      agreementsItems[0]?.original_name === ASSET_NO_AGREEMENT_ATTACHMENTS_NAME
    ) {
      notApplicableAgreement = agreementsItems[0].not_applicable;
      uploadLaterAgreement = agreementsItems[0].upload_later;
    } else {
      agreements = agreementsItems.map((attachment) => ({
        uniqueId: attachment.unique_id,
        originalName: attachment.original_name,
        url: attachment.url,
        size: attachment.size,
        type: attachment.type,
      }));
    }

    const invoicesItems = dataDto.attachments.filter(
      ({ type }) => type === "invoice",
    );
    let notApplicableInvoice,
      uploadLaterInvoice,
      invoices: AssetAttachmentModel[] = [];
    if (
      invoicesItems[0]?.original_name === null ||
      invoicesItems[0]?.original_name === ASSET_NO_INVOICE_ATTACHMENTS_NAME
    ) {
      notApplicableInvoice = invoicesItems[0].not_applicable;
      uploadLaterInvoice = invoicesItems[0].upload_later;
    } else {
      invoices = invoicesItems.map((attachment) => ({
        uniqueId: attachment.unique_id,
        originalName: attachment.original_name,
        url: attachment.url,
        size: attachment.size,
        type: attachment.type,
      }));
    }

    const disposalItems = dataDto.attachments.filter(
      ({ type, not_applicable, upload_later }) =>
        !type && !not_applicable && !upload_later,
    );
    const disposal: AssetAttachmentModel[] = disposalItems.map(
      (attachment) => ({
        uniqueId: attachment.unique_id,
        originalName: attachment.original_name,
        url: attachment.url,
        size: attachment.size,
        type: attachment.type,
      }),
    );

    const data = prepareAssetToModel(dataDto);
    data.attachments = [...agreements, ...invoices, ...disposal];
    data.notApplicableAgreement = notApplicableAgreement;
    data.uploadLaterAgreement = uploadLaterAgreement;
    data.notApplicableInvoice = notApplicableInvoice;
    data.uploadLaterInvoice = uploadLaterInvoice;

    return data;
  };

  return useQuery<AssetModel, ApiErrorModel>({
    queryKey: ["assetGetOne", id],
    queryFn,
    enabled: !!id,
  });
};

export const useAssetCreate = (
  onSuccess?: (assetId: string) => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    businessId,
    type,
    purchaseType,
    name,
    date,
    firstLetAt,
    cost,
    jointShare,
    comment,
    notApplicableAgreement,
    uploadLaterAgreement,
    filesAgreement,
    notApplicableInvoice,
    uploadLaterInvoice,
    filesInvoice,
    isDefault,
  }: AssetFormInterface) => {
    const files = prepareAssetFiles({
      filesAgreement,
      notApplicableAgreement,
      uploadLaterAgreement,
      filesInvoice,
      notApplicableInvoice,
      uploadLaterInvoice,
    });

    const fieldsDto: AssetFormInterfaceDto = {
      user_business_unique_id: businessId,
      type,
      purchase_type: purchaseType,
      name,
      date: formatDateForApi(date),
      first_let_at: firstLetAt ? formatDateForApi(firstLetAt) : undefined,
      cost,
      joint_share: jointShare,
      comment,
      files,
      is_default: isDefault,
    };

    const { data: dataDto } = await api.post<{ asset_unique_id: string }>(
      "/assets",
      fieldsDto,
    );
    return dataDto.asset_unique_id;
  };

  return useMutation({
    mutationKey: ["assetCreate"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        businessId: error.fields?.user_business_unique_id || "",
        type: error.fields?.type || "",
        purchaseType: error.fields?.purchase_type || "",
        name: error.fields?.name || "",
        date: error.fields?.date || "",
        firstLetAt: error.fields?.first_let_at || "",
        cost: error.fields?.cost || "",
        jointShare: error.fields?.joint_share || "",
        comment: error.fields?.comment || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useAssetUpdate = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    assetId,
    businessId,
    type,
    purchaseType,
    name,
    date,
    firstLetAt,
    cost,
    jointShare,
    comment,
    notApplicableAgreement,
    uploadLaterAgreement,
    filesAgreement,
    notApplicableInvoice,
    uploadLaterInvoice,
    filesInvoice,
    attachments,
    isDefault,
  }: AssetFormInterface) => {
    const files = prepareAssetFiles({
      filesAgreement,
      notApplicableAgreement,
      uploadLaterAgreement,
      filesInvoice,
      notApplicableInvoice,
      uploadLaterInvoice,
    });

    const fieldsDto: AssetFormInterfaceDto = {
      user_business_unique_id: businessId,
      type,
      purchase_type: purchaseType,
      name,
      date: formatDateForApi(date),
      first_let_at: firstLetAt ? formatDateForApi(firstLetAt) : undefined,
      cost,
      joint_share: jointShare,
      comment,
      attachments: attachments?.map((attachment) => ({
        unique_id: attachment.uniqueId,
      })),
      files,
      is_default: isDefault,
    };

    await api.put(`/assets/${assetId}`, fieldsDto);
  };

  return useMutation({
    mutationKey: ["assetUpdate"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        businessId: error.fields?.user_business_unique_id || "",
        type: error.fields?.type || "",
        purchaseType: error.fields?.purchase_type || "",
        name: error.fields?.name || "",
        date: error.fields?.date || "",
        firstLetAt: error.fields?.first_let_at || "",
        cost: error.fields?.cost || "",
        jointShare: error.fields?.joint_share || "",
        comment: error.fields?.comment || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useAssetDispose = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    assetId,
    date,
    amount,
    comment,
    files,
  }: AssetDisposalFormInterface) => {
    const fieldsDto: AssetDisposalFormInterfaceDto = {
      disposal_date: formatDateForApi(date),
      disposal_cost: amount,
      disposal_comment: comment,
      files: !files?.length
        ? []
        : files.map((file) => ({
            key: file.key,
            uuid: file.uuid,
            original_name: file.originalName,
          })),
    };
    await api.post(`/assets/${assetId}/dispose`, fieldsDto);
  };

  return useMutation({
    mutationKey: ["assetDispose"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      if (onError) {
        onError({ ...error });
      }
    },
  });
};
